// =============================================================================
// The header's cart badge.
//
// WooCommerce's cart fragments keep the badge current for every classic cart
// action, but the Store API add-to-cart path this theme's cards use bypasses
// them entirely — the response comes straight back to the script, and nothing
// else on the page hears about it. This writes the number from that response.
//
// It mirrors `aug_cart_badge_html()` exactly: the count as text content, and the
// `hidden` attribute when empty. Two writers of one badge that disagree about
// its markup would flicker.
// =============================================================================

/**
 * Paint a cart total into every badge on the page.
 *
 * More than one exists — the header renders one and the mobile drawer another —
 * and a fragment refresh replaces both, so this has to as well.
 *
 * @param {number} count Total item quantity in the cart.
 */
function setCartCount(count) {
	const total = Number(count);

	if (!Number.isFinite(total) || total < 0) {
		return;
	}

	document.querySelectorAll("[data-cart-count]").forEach((badge) => {
		badge.textContent = String(total);

		// The attribute, not an inline display rule: `.cart-count[hidden]` is
		// already overridden in the stylesheet, and a fragment swap would
		// discard any inline style this set.
		if (total > 0) {
			badge.removeAttribute("hidden");
		} else {
			badge.setAttribute("hidden", "");
		}
	});
}

/**
 * Read the item count out of a Store API cart response.
 *
 * Every mutating Store API endpoint returns the whole cart, so `items_count` is
 * available on add, update and remove alike.
 *
 * @param {unknown} cart A Store API cart payload.
 * @return {number|null} The count, or null when the payload does not carry one.
 */
function cartCountFrom(cart) {
	const count = Number(cart?.items_count);

	return Number.isFinite(count) ? count : null;
}

/**
 * Paint a count from a Store API response, ignoring payloads that lack one.
 *
 * @param {unknown} cart A Store API cart payload.
 */
export function syncCartCount(cart) {
	const count = cartCountFrom(cart);

	if (count !== null) {
		setCartCount(count);
	}
}
